/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * What the nine Pulse pages share: the load hooks (one page, or a cursor-paged list), the query-string state a deep link
 * lands on (`?record=`, `?problem=`), the filter controls every list carries (date range, domain, surface), the Export
 * NDJSON link, the chips from the vocabularies, the bar rows the Overview and the scorecards draw, and the anonymity
 * sentence.
 */
import { Fragment, useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { Download } from 'lucide-react';
import {
  DEFAULT_RANGE_DAYS, DOMAIN_LABELS, PULSE_DOMAINS, RANGE_OPTIONS, SURFACE_OPTIONS, pulseReadFailure, type PulseChip, type PulseList,
} from '../../lib/pulse';
import { ApiError } from '../../lib/api';
import { linkClick, navigate, usePlace } from '../../shell/router';
import { ErrorState, LinkButton, Meter, Select, StatusChip } from '../../ui';

export type Load<T> = { state: 'loading' } | { state: 'ready'; data: T } | { state: 'failed'; error: unknown };

/**
 * Loads once and again whenever `key` changes (the filters, serialized). A reload keeps the data on screen while it runs
 * and says so through `reloading`; a failed reload keeps the data and reports the error in `reloadError`.
 */
export function useLoad<T>(fetcher: () => Promise<T>, key: string): {
  load: Load<T>; reload: () => void; reloading: boolean; reloadError: unknown; setData: (update: (data: T) => T) => void;
} {
  const [load, setLoad] = useState<Load<T>>({ state: 'loading' });
  const [reloading, setReloading] = useState(false);
  const [reloadError, setReloadError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  const fetch = useRef(fetcher);
  useEffect(() => {
    fetch.current = fetcher;
  });
  const lastKey = useRef<string | null>(null);
  useEffect(() => {
    let live = true;
    const fresh = lastKey.current !== key;
    lastKey.current = key;
    if (fresh) setLoad({ state: 'loading' });
    else setReloading(true);
    setReloadError(null);
    fetch.current().then(
      (data) => {
        if (!live) return;
        setLoad({ state: 'ready', data });
        setReloading(false);
      },
      (error: unknown) => {
        if (!live) return;
        setLoad((current) => (fresh || current.state !== 'ready' ? { state: 'failed', error } : current));
        setReloadError(error);
        setReloading(false);
      },
    );
    return () => {
      live = false;
    };
  }, [key, tick]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  const setData = useCallback((update: (data: T) => T) => {
    setLoad((current) => (current.state === 'ready' ? { state: 'ready', data: update(current.data) } : current));
  }, []);
  return { load, reload, reloading, reloadError, setData };
}

/**
 * A cursor-paged list: the first page loads with `key` (the filters and sort, serialized), "Load more" appends the next
 * page, and a change of key starts over. `setItems` edits a row in place after a write (marking a feedback entry reviewed).
 */
export function usePagedList<T>(fetcher: (cursor?: string) => Promise<PulseList<T>>, key: string): {
  load: Load<PulseList<T>>; reload: () => void; reloading: boolean; items: T[]; next: string | null; total: number;
  loadMore: (cursor: string) => void; moreLoading: boolean; moreError: unknown;
  /** Maps the rows in place after a write (a row marked reviewed); never adds a row: that is `prepend`. */
  setItems: (update: (items: T[]) => T[]) => void;
  /** A new row at the top (a digest run now, a problem opened). */
  prepend: (item: T) => void;
} {
  const first = useLoad(() => fetcher(), key);
  const { setData } = first;
  const [extra, setExtra] = useState<{ key: string; items: T[]; next: string | null }>({ key, items: [], next: null });
  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const fetch = useRef(fetcher);
  useEffect(() => {
    fetch.current = fetcher;
  });
  const extraItems = extra.key === key ? extra.items : [];
  const items = first.load.state === 'ready' ? [...first.load.data.items, ...extraItems] : [];
  const next = extra.key === key && extra.items.length > 0 ? extra.next : first.load.state === 'ready' ? first.load.data.next : null;
  const total = first.load.state === 'ready' ? first.load.data.total : 0;
  const loadMore = useCallback((cursor: string) => {
    setMoreLoading(true);
    setMoreError(null);
    fetch.current(cursor).then(
      (page) => {
        setExtra((current) => ({ key, items: [...(current.key === key ? current.items : []), ...page.items], next: page.next }));
        setMoreLoading(false);
      },
      (error: unknown) => {
        setMoreError(error);
        setMoreLoading(false);
      },
    );
  }, [key]);
  const setItems = useCallback((update: (list: T[]) => T[]) => {
    setData((data) => ({ ...data, items: update(data.items) }));
    setExtra((current) => ({ ...current, items: update(current.items) }));
  }, [setData]);
  const prepend = useCallback((item: T) => {
    setData((data) => ({ ...data, items: [item, ...data.items], total: data.total + 1 }));
  }, [setData]);
  return { load: first.load, reload: first.reload, reloading: first.reloading, items, next, total, loadMore, moreLoading, moreError, setItems, prepend };
}

/** One query-string value of the page's own address (`?record=pa_…`), kept in the history entry so Back closes a drawer. */
export function useQueryParam(name: string): [string | null, (value: string | null, opts?: { replace?: boolean }) => void] {
  const place = usePlace();
  const value = new URLSearchParams(place.search).get(name);
  const set = useCallback((next: string | null, opts: { replace?: boolean } = {}) => {
    const params = new URLSearchParams(window.location.search);
    if (next === null || next === '') params.delete(name);
    else params.set(name, next);
    const query = params.toString();
    navigate(`${window.location.pathname}${query ? `?${query}` : ''}`, { replace: opts.replace ?? true });
  }, [name]);
  return [value, set];
}

/** The date range, domain and surface every list takes (`''` is "all"). */
export interface PulseFilters { days: number; domain: string; surface: string }
export const DEFAULT_FILTERS: PulseFilters = { days: DEFAULT_RANGE_DAYS, domain: '', surface: '' };

/** The filters as the routes take them: nothing for "all". */
export function filterParams(filters: PulseFilters): { days: number; domain?: string; surface?: string } {
  return { days: filters.days, domain: filters.domain || undefined, surface: filters.surface || undefined };
}

export function RangeSelect({ value, onChange, size = 'sm' }: { value: number; onChange: (days: number) => void; size?: 'sm' | 'md' }) {
  return (
    <Select size={size} className="ui-w-auto" aria-label="Date range" value={String(value)} onChange={(event) => onChange(Number(event.target.value))}>
      {RANGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
    </Select>
  );
}

export function DomainSelect({ value, onChange, all = true, size = 'sm', label = 'Domain' }: { value: string; onChange: (domain: string) => void; all?: boolean; size?: 'sm' | 'md'; label?: string }) {
  return (
    <Select size={size} className="ui-w-auto" aria-label={label} value={value} onChange={(event) => onChange(event.target.value)}>
      {all ? <option value="">Domain: All</option> : null}
      {PULSE_DOMAINS.map((domain) => <option key={domain} value={domain}>{DOMAIN_LABELS[domain]}</option>)}
    </Select>
  );
}

export function SurfaceSelect({ value, onChange, size = 'sm' }: { value: string; onChange: (surface: string) => void; size?: 'sm' | 'md' }) {
  return (
    <Select size={size} className="ui-w-auto" aria-label="Surface" value={value} onChange={(event) => onChange(event.target.value)}>
      <option value="">Surface: All</option>
      {SURFACE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
    </Select>
  );
}

/** The three filters together, as the page header's meta or a panel's toolbar. */
export function FilterBar({ filters, onChange, surface = true, domain = true }: { filters: PulseFilters; onChange: (next: PulseFilters) => void; surface?: boolean; domain?: boolean }) {
  return (
    <>
      <RangeSelect value={filters.days} onChange={(days) => onChange({ ...filters, days })} />
      {domain ? <DomainSelect value={filters.domain} onChange={(value) => onChange({ ...filters, domain: value })} /> : null}
      {surface ? <SurfaceSelect value={filters.surface} onChange={(value) => onChange({ ...filters, surface: value })} /> : null}
    </>
  );
}

/** Export NDJSON: a download of the list as filtered (the audited export route). */
export function ExportButton({ href, title = 'Download this list as NDJSON, filtered as shown; the export ends in a verification line and is itself audited.' }: { href: string; title?: string }) {
  return <LinkButton size="sm" icon={Download} href={href} download title={title}>Export NDJSON</LinkButton>;
}

/** A chip from one of lib/pulse.ts's maps. */
export function Chip({ spec, plain = false }: { spec: PulseChip; plain?: boolean }) {
  return <StatusChip tone={spec.tone} plain={plain}>{spec.label}</StatusChip>;
}

export interface BarRowProps {
  label: ReactNode;
  /** 0 to 1. */
  value: number;
  figure: ReactNode;
  meterLabel: string;
  tone?: 'data' | 'accent' | 'ok' | 'warn' | 'bad';
  /** The label becomes a link that navigates in place. */
  href?: string;
}

/** A labelled bar with its figure on the right: the helped rate by domain, the feedback split, the reasons. */
export function BarRow({ label, value, figure, meterLabel, tone = 'data', href }: BarRowProps) {
  return (
    <div className="pulse-bar-row">
      <span>{href ? <PageLink href={href}>{label}</PageLink> : label}</span>
      <Meter value={value} label={meterLabel} tone={tone} size="sm" />
      <span className="pulse-bar-figure">{figure}</span>
    </div>
  );
}

/** A link to another console page (or a place on one), navigating in place; Ctrl-click still opens a tab. */
export function PageLink({ href, children, className, title, onNavigate }: { href: string; children: ReactNode; className?: string; title?: string; onNavigate?: () => void }) {
  return <a href={href} className={className} title={title} onClick={(event: MouseEvent<HTMLAnchorElement>) => linkClick(event, onNavigate)}>{children}</a>;
}

/** A failed load, in the service's words (a 404 read as the dark service) with its status, and Try again. */
export function PulseError({ title, error, onRetry, retrying }: { title: ReactNode; error: unknown; onRetry?: () => void; retrying?: boolean }) {
  const status = (error as { status?: unknown } | null)?.status;
  const said = new ApiError(pulseReadFailure(error), typeof status === 'number' ? status : 0);
  return <ErrorState compact title={title} error={said} onRetry={onRetry} retrying={retrying} />;
}

/** The drawer's last line (strategy 9.5): nothing in Pulse names a person. */
export function AnonymousFoot({ what = 'entry' }: { what?: string }) {
  return <p className="pulse-foot">{`No person, device or conversation is recorded for this ${what}.`}</p>;
}

/** A definition list of facts, the drawers' top block (the kit's `ui-facts` grid of terms and details). */
export function Facts({ rows }: { rows: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="ui-facts">
      {rows.map(([term, detail], index) => (
        <Fragment key={index}>
          <dt>{term}</dt>
          <dd>{detail}</dd>
        </Fragment>
      ))}
    </dl>
  );
}
